// Who may move money on a customer's Money tab, and how a payment method is said (gap #8).

import type { CardCheck, PaymentReceipt } from './history-model.ts';

/**
 * Who may move money on the Money tab (gap #8): refund and record a dispute (payments.refund), use a
 * credit or keep a draft's payment as credit (invoices.edit), write off a bad debt (invoices.writeOff,
 * the owner's unless the owner grants it), void a payment recorded in error (payments.void), record a
 * payment the customer made (payments.record: the office, bar the warehouse).
 */
/**
 * Who may move money here. While the company mirrors ServiceTitan (`mirroring`), its payments,
 * refunds, credits and write-offs are ServiceTitan's (st-mirror §2: taken there), so none of these.
 */
export function moneyRights(held: readonly string[], mirroring = false) {
  // by the permission each action asks for, whichever role gives it (QA-681)
  const has = (p: string) => !mirroring && held.includes(p);
  return {
    refund: has('payments.refund'),
    edit: has('invoices.edit'),
    writeOff: has('invoices.writeOff'),
    void: has('payments.void'),
    record: has('payments.record'),
  };
}

const METHOD: Record<string, string> = {
  card: 'card',
  etransfer: 'e-transfer',
  cheque: 'cheque',
  cash: 'cash',
  eft: 'EFT',
  financing: 'financing',
  other: 'another way',
};
/** How a payment came in or goes back, in words ("etransfer" → "e-transfer"). */
export const methodWord = (m: string) => METHOD[m] ?? m.replace(/_/g, ' ');

/**
 * An amount typed over what a money sheet allows, said beside it (QA-716): "That’s more than the
 * $200.00 paid." rather than a button that says $0.00. Null when it fits, or isn't an amount yet.
 */
export function overWords(typedCents: number | null, maxCents: number, what: string): string | null {
  if (typedCents === null || typedCents <= maxCents) return null;
  const cad = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(maxCents / 100);
  return `That’s more than the ${cad} ${what}.`;
}

const STATUS: Record<string, string> = {
  draft: 'Draft',
  finalized: 'Finalized',
  sent: 'Sent',
  partially_paid: 'Partly paid',
  paid: 'Paid',
  void: 'Void',
  written_off: 'Written off',
};
/** An invoice's status in words, as Review says it ("partially_paid" → "Partly paid"). */
export const invoiceStatusWord = (s: string) =>
  STATUS[s] ?? s.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

/**
 * Whether Void is worth offering: a card payment is refunded instead, and a disputed one is settled by
 * the dispute. The server refuses the rest (deposited, refunded, credit used, imported) in words.
 */
export const voidable = (p: { method: string; status: string }) =>
  p.method !== 'card' && p.status === 'recorded';

const VOID_INSTEAD: Record<string, string> = {
  card: 'Use Refund… to give it back to the card.',
  disputed: 'Record how the dispute ends with Dispute… instead.',
};
/** The server's refusal of a void, and what to do instead (detail.reason from payments.void). */
export function voidRefusal(message: string, reason: string | undefined): string {
  if (!reason) return message;
  return `${message} ${VOID_INSTEAD[reason] ?? 'If the customer is owed money back, use Refund… instead.'}`;
}

const cad = (cents: number) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(cents / 100);

/**
 * A card charge still being checked, said where the invoice is (QA-307): "A card payment of $60.00,
 * started Oct 16, 2026, 10:42 a.m., is still being checked…". What isn't known is left out.
 */
export function cardCheckWords(check: CardCheck, timeZone?: string): string {
  const when =
    check.startedAt && timeZone
      ? new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'medium', timeStyle: 'short' }).format(
          new Date(check.startedAt),
        )
      : null;
  return [
    'A card payment',
    check.amountCents !== undefined ? ` of ${cad(check.amountCents)}` : '',
    check.cardLast4 ? ` on the card ending ${check.cardLast4}` : '',
    when ? `, started ${when},` : '',
    ' is still being checked with the card processor. Nothing else can be taken on this invoice until it is.',
  ].join('');
}

/** What payments.checkCard and payments.cancelUnknown answer. */
export interface CardAnswer {
  state: 'open' | 'approved' | 'declined' | 'expired' | 'unknown';
  amountCents: number;
  message: string | null;
}

/** The processor's answer in words: the server's own where it has them. */
export function cardAnswerWords(r: CardAnswer, asked: 'check' | 'cancel'): string {
  if (r.state === 'approved')
    return `The card payment of ${cad(r.amountCents)} went through and is recorded.`;
  if (r.state === 'declined' || r.state === 'expired') return r.message ?? 'Nothing was charged.';
  // a cancel the processor hasn't confirmed says so, and what happens next
  if (asked === 'cancel' && r.message) return r.message;
  return 'The card processor still hasn’t answered. Check again in a few minutes.';
}

const CHANNEL = { email: 'email', sms: 'text' } as const;
const NOT_SENT: Record<NonNullable<PaymentReceipt['reason']>, string> = {
  off: 'receipts were turned off',
  void: 'voided',
  refunded: 'refunded',
  do_not_contact: 'do-not-contact',
  no_consented_address: 'no email or texts they agreed to',
  no_invoice: 'not on an invoice',
  renewal: 'the renewal’s own message covers it',
};

/**
 * A payment's receipt in words: "Receipt sent by email at 10:42 a.m." (with the day when it wasn't
 * today), "Receipt by text didn't go through…", "Receipt by text goes at 8:00 a.m., after quiet hours",
 * or "No receipt: do-not-contact".
 */
export function receiptWords(r: PaymentReceipt, timeZone: string, now = new Date()): string {
  if (r.outcome === 'skipped') return `No receipt: ${r.reason ? NOT_SENT[r.reason] : 'not sent'}`;
  const day = (d: Date) => new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'medium' }).format(d);
  const at = new Date(r.at);
  const time = new Intl.DateTimeFormat('en-CA', { timeZone, timeStyle: 'short' }).format(at);
  const when = day(at) === day(now) ? `at ${time}` : `on ${day(at)} at ${time}`;
  const by = r.channel ? ` by ${CHANNEL[r.channel]}` : '';
  if (r.outcome === 'held') return `Receipt${by} goes ${when}, after quiet hours`;
  return r.failed ? `Receipt${by} didn’t go through (sent ${when})` : `Receipt sent${by} ${when}`;
}

/** Whether Send again is worth offering: a customer it can go to, and a payment it would go for. */
export function canResend(
  p: { status: string; receipt?: PaymentReceipt | null },
  channel: 'email' | 'sms' | null | undefined,
): boolean {
  if (!channel || p.status === 'void') return false;
  return !['void', 'refunded', 'no_invoice', 'renewal'].includes(p.receipt?.reason ?? '');
}

/** What Send again did, in words. */
export const resentWords = (channel: 'email' | 'sms', again: boolean) =>
  `Receipt sent${again ? ' again' : ''} by ${CHANNEL[channel]}.`;

/**
 * How many invoices or payments the Money tab shows at first, and adds with each "Show more" (history
 * fix 4, 22 Oct: a customer with 2,499 invoices rendered and read every one of them at once).
 */
export const MONEY_PAGE = 50;

/** The first `shown` rows, and how many more there are. A row asked for by a link (`focus`) is always in. */
export function moneyPage<T extends { id: string }>(
  rows: readonly T[],
  shown: number,
  focus?: string,
): { rows: T[]; more: number } {
  const at = focus ? rows.findIndex((r) => r.id === focus) : -1;
  const n = Math.max(shown, at + 1);
  return { rows: rows.slice(0, n), more: Math.max(0, rows.length - n) };
}

/** "Show 50 more · 2,449 left", or "Show the last 12". */
export const showMoreText = (more: number): string =>
  more > MONEY_PAGE
    ? `Show ${MONEY_PAGE} more · ${more.toLocaleString('en-CA')} left`
    : `Show the last ${more}`;

/**
 * The payments the tab lists, and how many $0.00 ones it leaves out: ServiceTitan's records of nothing
 * paid (history fix 4; one recorded here is never $0), kept on the record but noise in the list.
 */
export function listedPayments<T extends { amountCents: number }>(
  payments: readonly T[],
): { payments: T[]; zero: number } {
  const listed = payments.filter((p) => p.amountCents !== 0);
  return { payments: listed, zero: payments.length - listed.length };
}
